import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import { ChevronLeft, ChevronRight, Filter, Loader2 } from "lucide-react";
import { istDateStr, readCache, writeCache } from "@/lib/ist";

const PAGE_SIZE = 30;
const FIRST_CACHE_KEY = "jt_prev_first_v1"; // { day, items, total }

function fmt(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return {
    day: d.toLocaleDateString("en-IN", { weekday: "short" }),
    date: d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
  };
}

function initialFromCache() {
  const c = readCache(FIRST_CACHE_KEY);
  if (c && c.day === istDateStr() && Array.isArray(c.items) && c.items.length > 0) {
    return { items: c.items, total: c.total ?? c.items.length, warm: true };
  }
  return { items: [], total: 0, warm: false };
}

export default function PreviousResult() {
  const initial = useRef(initialFromCache()).current;

  const [items, setItems] = useState(initial.items);
  const [total, setTotal] = useState(initial.total);
  const [page, setPage] = useState(1);
  const [year, setYear] = useState("");
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(!initial.warm);
  const [firstLoadDone, setFirstLoadDone] = useState(initial.warm);
  const [yearCounts, setYearCounts] = useState({}); // {year: count}

  const inFlightKey = useRef(null);
  const prefetchCache = useRef(new Map());

  useSEO({
    title: "Juwai Teer Previous Result | FR & SR Result Archive",
    description:
      "Complete Juwai Teer Previous Result archive. Browse First Round (FR) and Second Round (SR) results by date and year — a searchable historical record of daily Juwai Teer announcements.",
    canonical: "https://jowaiteer.com/previous-result/",
  });

  useEffect(() => {
    let alive = true;
    api.get("/results/years").then(({ data }) => {
      if (alive) setYears(data.years || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Lightweight per-year count enrichment (small extra fetch, once)
  useEffect(() => {
    if (!years.length) return;
    let alive = true;
    Promise.all(
      years.map((y) =>
        api.get(`/results/previous?year=${y}&page=1&page_size=1`)
          .then(({ data }) => [y, data.total || 0])
          .catch(() => [y, 0])
      )
    ).then((entries) => { if (alive) setYearCounts(Object.fromEntries(entries)); });
    return () => { alive = false; };
  }, [years]);

  const buildKey = useCallback((y, p) => `${y || ""}|${p}`, []);
  const buildUrl = useCallback((y, p) => {
    const params = new URLSearchParams();
    params.set("page", String(p));
    params.set("page_size", String(PAGE_SIZE));
    if (y) params.set("year", String(y));
    return `/results/previous?${params.toString()}`;
  }, []);

  const load = useCallback(async (y, p, { showSpinner }) => {
    const key = buildKey(y, p);
    const cached = prefetchCache.current.get(key);
    if (cached) {
      setItems(cached.items);
      setTotal(cached.total);
      setLoading(false);
      setFirstLoadDone(true);
      inFlightKey.current = key;
      try {
        const { data } = await api.get(buildUrl(y, p));
        if (inFlightKey.current === key) {
          setItems(data.items || []);
          setTotal(data.total || 0);
          prefetchCache.current.set(key, { items: data.items || [], total: data.total || 0 });
        }
      } catch (_e) { /* ignore */ }
      finally { if (inFlightKey.current === key) inFlightKey.current = null; }
      return;
    }
    if (inFlightKey.current === key) return;
    inFlightKey.current = key;
    if (showSpinner) setLoading(true);
    try {
      const { data } = await api.get(buildUrl(y, p));
      if (inFlightKey.current !== key) return;
      const nextItems = data.items || [];
      setItems(nextItems);
      setTotal(data.total || 0);
      prefetchCache.current.set(key, { items: nextItems, total: data.total || 0 });
      if (!y && p === 1) {
        writeCache(FIRST_CACHE_KEY, { day: istDateStr(), items: nextItems, total: data.total || 0 });
      }
    } catch (_e) {
      setItems([]); setTotal(0);
    } finally {
      if (inFlightKey.current === key) inFlightKey.current = null;
      setLoading(false);
      setFirstLoadDone(true);
    }
  }, [buildKey, buildUrl]);

  useEffect(() => {
    const key = buildKey(year, page);
    const havePrefetch = prefetchCache.current.has(key);
    const useWarmForFirst = year === "" && page === 1 && initial.warm && items.length > 0;
    load(year, page, { showSpinner: !havePrefetch && !useWarmForFirst });
  }, [year, page, load, buildKey, initial.warm, items.length]);

  useEffect(() => {
    if (page !== 1 || year !== "" || items.length === 0) return;
    const key = buildKey("", 2);
    if (prefetchCache.current.has(key) || inFlightKey.current === key) return;
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 250));
    const handle = idle(() => {
      api.get(buildUrl("", 2)).then(({ data }) => {
        prefetchCache.current.set(key, { items: data.items || [], total: data.total || 0 });
      }).catch(() => {});
    });
    return () => { if (window.cancelIdleCallback && typeof handle === "number") window.cancelIdleCallback(handle); };
  }, [page, year, items.length, buildKey, buildUrl]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showingFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(page * PAGE_SIZE, total);

  const pageNumbers = useMemo(() => {
    const set = new Set([1, totalPages, page - 1, page, page + 1]);
    return [...set].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  }, [page, totalPages]);

  const displayValue = (v) => (v == null ? "XX" : String(v).padStart(2, "0"));

  // Derived enrichment data for text content (real, not fabricated)
  const earliestYear = years.length ? Math.min(...years) : null;
  const latestYear = years.length ? Math.max(...years) : null;
  const totalArchive = Object.values(yearCounts).reduce((a, b) => a + b, 0) || total;

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://jowaiteer.com/" },
      { "@type": "ListItem", position: 2, name: "Previous Result", item: "https://jowaiteer.com/previous-result/" },
    ],
  };
  const datasetJsonLd = totalArchive > 0 ? {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: "Juwai Teer Previous Result Archive",
    description: `A historical archive of ${totalArchive} Juwai Teer First Round (FR) and Second Round (SR) result records${earliestYear && latestYear ? ` covering ${earliestYear} through ${latestYear}` : ""}.`,
    url: "https://jowaiteer.com/previous-result/",
    license: "https://creativecommons.org/licenses/by/4.0/",
    keywords: ["Juwai Teer", "Juwai Teer Previous Result", "Juwai Teer FR SR", "Teer result archive"],
    creator: { "@type": "Organization", name: "Juwai Teer", url: "https://jowaiteer.com/" },
    variableMeasured: [
      { "@type": "PropertyValue", name: "First Round (FR)", description: "Announced daily at 2:10 PM IST" },
      { "@type": "PropertyValue", name: "Second Round (SR)", description: "Announced daily at 3:10 PM IST" },
    ],
  } : null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-8">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      {datasetJsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(datasetJsonLd) }} />
      )}

      {/* Breadcrumbs (visible) */}
      <nav aria-label="Breadcrumb" className="text-xs text-[color:var(--brand-purple)]/80 mb-3">
        <a href="/" className="link-underline font-semibold">Home</a>
        <span className="mx-2">›</span>
        <span className="font-semibold text-[color:var(--brand-purple-deep)]">Previous Result</span>
      </nav>

      <div className="text-center rise-in">
        <div className="ornament justify-center mb-3">
          <span className="line" /><span className="arrow" />
          <span className="uppercase text-xs font-bold tracking-widest text-[color:var(--brand-orange)]">Archive</span>
          <span className="arrow" /><span className="line" />
        </div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[color:var(--brand-purple-deep)]">Juwai Teer Previous Result</h1>
        <p className="mt-2 text-sm text-[color:var(--brand-purple)]/85">
          Newest first · First Round (FR) and Second Round (SR) history
        </p>
      </div>

      {/* Toolbar */}
      <div className="card-soft mt-4 p-3 sm:p-4 flex flex-wrap gap-3 items-center justify-between" data-testid="archive-toolbar">
        <div className="flex items-center gap-2 text-sm">
          <Filter size={16} className="text-[color:var(--brand-purple)]" />
          <label htmlFor="year-select" className="font-semibold text-[color:var(--brand-purple-deep)]">Year:</label>
          <select
            id="year-select"
            data-testid="year-filter"
            value={year}
            onChange={(e) => { setPage(1); setYear(e.target.value); }}
            className="border border-[hsl(var(--border))] rounded-lg px-2 py-1.5 bg-white text-[color:var(--brand-purple-deep)] font-semibold"
          >
            <option value="">All</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="text-xs text-[color:var(--brand-purple)]/80" data-testid="archive-counter">
          {loading && items.length === 0 ? "Loading" : (
            total === 0 ? "No results yet" : <>Showing <strong>{showingFrom}&ndash;{showingTo}</strong> of <strong>{total}</strong></>
          )}
        </div>
      </div>

      <div className="mt-4 grid gap-2" data-testid="previous-result-list">
        <div className="grid grid-cols-[1.2fr_1fr_1fr] px-4 text-[11px] tracking-widest font-bold text-[color:var(--brand-purple)]/70 uppercase">
          <div>Date</div>
          <div className="text-center">First Round</div>
          <div className="text-center">Second Round</div>
        </div>

        {items.length === 0 && loading && (
          <>
            {[0, 1, 2].map((i) => (
              <div key={i} className="result-row animate-pulse">
                <div className="h-4 bg-[color:var(--brand-purple-soft)] rounded w-24" />
                <div className="h-4 bg-[color:var(--brand-purple-soft)] rounded w-8 mx-auto" />
                <div className="h-4 bg-[color:var(--brand-purple-soft)] rounded w-8 mx-auto" />
              </div>
            ))}
          </>
        )}

        {items.length === 0 && !loading && (
          <div className="card-soft p-8 text-center text-sm text-[color:var(--brand-purple)]/80" data-testid="previous-empty">
            {year ? `No results published for ${year} yet.` : "The archive is currently being updated."}
          </div>
        )}

        {items.map((r) => {
          const f = fmt(r.date);
          return (
            <div key={r.id || r.date} className="result-row" data-testid={`row-${r.date}`}>
              <div>
                <div className="font-bold text-[color:var(--brand-purple-deep)] text-sm">{f.date}</div>
                <div className="text-[11px] uppercase tracking-wider text-[color:var(--brand-purple)]/70">{f.day}</div>
              </div>
              <div className="text-center">
                <span className="inline-block min-w-[52px] border-2 border-[color:var(--brand-purple)] rounded-lg py-1.5 px-2 font-display font-extrabold text-lg text-[color:var(--brand-purple-deep)]">
                  {displayValue(r.fr)}
                </span>
              </div>
              <div className="text-center">
                <span className="inline-block min-w-[52px] border-2 border-[color:var(--brand-purple)] rounded-lg py-1.5 px-2 font-display font-extrabold text-lg text-[color:var(--brand-purple-deep)]">
                  {displayValue(r.sr)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {total > PAGE_SIZE && (
        <nav className="mt-6 flex items-center justify-center gap-1 flex-wrap" aria-label="Archive pagination" data-testid="archive-pagination">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[hsl(var(--border))] text-sm font-semibold text-[color:var(--brand-purple-deep)] hover:bg-[color:var(--brand-purple-soft)] disabled:opacity-50" data-testid="page-prev"><ChevronLeft size={14} /> Prev</button>
          {pageNumbers.map((p, i) => {
            const prev = pageNumbers[i - 1];
            const gap = prev !== undefined && p - prev > 1;
            return (
              <span key={p} className="flex items-center gap-1">
                {gap && <span className="px-1 text-[color:var(--brand-purple)]/60">…</span>}
                <button onClick={() => setPage(p)} aria-current={p === page ? "page" : undefined} className={`min-w-[36px] px-2 py-1.5 rounded-lg text-sm font-bold border ${p === page ? "bg-[color:var(--brand-purple)] text-white border-[color:var(--brand-purple)]" : "bg-white text-[color:var(--brand-purple-deep)] border-[hsl(var(--border))] hover:bg-[color:var(--brand-purple-soft)]"}`} data-testid={`page-${p}`}>{p}</button>
              </span>
            );
          })}
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[hsl(var(--border))] text-sm font-semibold text-[color:var(--brand-purple-deep)] hover:bg-[color:var(--brand-purple-soft)] disabled:opacity-50" data-testid="page-next">Next <ChevronRight size={14} /></button>
          {loading && firstLoadDone && items.length > 0 && (
            <span className="ml-2 inline-flex items-center gap-1 text-xs text-[color:var(--brand-purple)]/80"><Loader2 size={14} className="animate-spin" /> updating</span>
          )}
        </nav>
      )}

      {/* Intro paragraph — moved below the table so archive shows first */}
      <section className="card-soft mt-6 p-5 sm:p-6 text-sm leading-relaxed text-[color:var(--brand-purple)]/95 space-y-3">
        <p>
          This page is the complete <strong>Juwai Teer Previous Result</strong> archive.
          It records every announced Juwai Teer First Round (FR) and Second Round (SR)
          result date-by-date, sorted with the newest result at the top so returning readers
          can find yesterday&apos;s or last week&apos;s numbers without scrolling through the
          whole history. Older records remain fully accessible through the year filter and
          page navigation above.
        </p>
        <p>
          Juwai Teer is a traditional archery-based activity from the Jaintia Hills of
          Meghalaya, India. Two rounds are held publicly every day — <strong>FR at 2:10 PM (IST)</strong>
          and <strong>SR at 3:10 PM (IST)</strong>. The result of each round is recorded here shortly
          after it is announced. Dates that were not held or where an announcement was not
          available are preserved as <em>XX</em> so the archive stays faithful to what actually
          happened rather than filled with fabricated numbers.
        </p>
        {totalArchive > 0 && (
          <p data-testid="archive-summary">
            The archive currently contains <strong>{totalArchive.toLocaleString("en-IN")}</strong> records
            {earliestYear && latestYear ? <> covering <strong>{earliestYear}</strong> through <strong>{latestYear}</strong></> : null}.
          </p>
        )}
      </section>

      {/* Per-year breakdown — moved below */}
      {years.length > 0 && (
        <section className="card-soft mt-4 p-5 sm:p-6" aria-labelledby="year-breakdown-heading">
          <h2 id="year-breakdown-heading" className="font-display font-extrabold text-lg text-[color:var(--brand-purple-deep)]">Records by year</h2>
          <div className="h-[3px] w-12 rounded-full bg-[color:var(--brand-orange)] mt-2 mb-3" />
          <ul className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            {years.map((y) => (
              <li key={y}>
                <button
                  type="button"
                  onClick={() => { setPage(1); setYear(String(y)); window.scrollTo({ top: 250, behavior: "smooth" }); }}
                  className="w-full text-left px-3 py-2 rounded-xl border border-[hsl(var(--border))] bg-white hover:bg-[color:var(--brand-purple-soft)]"
                  data-testid={`year-tile-${y}`}
                >
                  <div className="font-bold text-[color:var(--brand-purple-deep)]">{y}</div>
                  <div className="text-[11px] text-[color:var(--brand-purple)]/75">
                    {yearCounts[y] != null ? `${yearCounts[y]} records` : "counting…"}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* How to read this archive */}
      <section className="card-soft mt-6 p-5 sm:p-6" aria-labelledby="how-to-read-heading">
        <h2 id="how-to-read-heading" className="font-display font-extrabold text-lg text-[color:var(--brand-purple-deep)]">How to read this archive</h2>
        <div className="h-[3px] w-12 rounded-full bg-[color:var(--brand-orange)] mt-2 mb-3" />
        <ul className="text-sm leading-relaxed text-[color:var(--brand-purple)]/95 space-y-1 list-disc ml-5">
          <li><strong>Date</strong> shows the calendar day the round was held (Asia/Kolkata).</li>
          <li><strong>First Round (FR)</strong> is the number announced at 2:10 PM.</li>
          <li><strong>Second Round (SR)</strong> is the number announced at 3:10 PM.</li>
          <li><em>XX</em> means the round was not held or the announced result was unavailable for that date. It is <em>not</em> a missing entry — it is a factual record of an unavailable state.</li>
          <li>Records are refreshed automatically after every announced round; no separate manual step is needed.</li>
        </ul>
      </section>

      {/* FAQ */}
      <section className="card-soft mt-4 p-5 sm:p-6" aria-labelledby="archive-faq-heading">
        <h2 id="archive-faq-heading" className="font-display font-extrabold text-lg text-[color:var(--brand-purple-deep)]">Frequently asked questions</h2>
        <div className="h-[3px] w-12 rounded-full bg-[color:var(--brand-orange)] mt-2 mb-4" />
        <div className="space-y-4 text-sm">
          <div>
            <div className="font-bold text-[color:var(--brand-purple-deep)]">How often is the Juwai Teer previous result page updated?</div>
            <div className="text-[color:var(--brand-purple)]/90">Every day, immediately after the FR (2:10 PM IST) and SR (3:10 PM IST) rounds are announced.</div>
          </div>
          <div>
            <div className="font-bold text-[color:var(--brand-purple-deep)]">How far back does the archive go?</div>
            <div className="text-[color:var(--brand-purple)]/90">
              {earliestYear && latestYear
                ? `The archive currently spans ${earliestYear}–${latestYear}. Older records may be added if verified data becomes available.`
                : "The archive is being updated. Please check back shortly."}
            </div>
          </div>
          <div>
            <div className="font-bold text-[color:var(--brand-purple-deep)]">Why do some rows show XX instead of a number?</div>
            <div className="text-[color:var(--brand-purple)]/90">XX marks a day when the round was not held or no result was announced. We preserve those days as XX rather than inventing numbers.</div>
          </div>
          <div>
            <div className="font-bold text-[color:var(--brand-purple-deep)]">Are these numbers predictions or guaranteed?</div>
            <div className="text-[color:var(--brand-purple)]/90">No. This page is a historical record of announced results. It is not a prediction, not a guarantee and not a suggestion of any future outcome.</div>
          </div>
          <div>
            <div className="font-bold text-[color:var(--brand-purple-deep)]">Where can I see today&apos;s live result?</div>
            <div className="text-[color:var(--brand-purple)]/90">On the <a className="link-underline font-semibold" href="/">Juwai Teer Result Today</a> homepage.</div>
          </div>
        </div>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context":"https://schema.org","@type":"FAQPage",
          "mainEntity":[
            {"@type":"Question","name":"How often is the Juwai Teer previous result page updated?","acceptedAnswer":{"@type":"Answer","text":"Every day, immediately after the FR (2:10 PM IST) and SR (3:10 PM IST) rounds are announced."}},
            {"@type":"Question","name":"How far back does the archive go?","acceptedAnswer":{"@type":"Answer","text": earliestYear && latestYear ? `The archive currently spans ${earliestYear}–${latestYear}.` : "The archive is being updated."}},
            {"@type":"Question","name":"Why do some rows show XX instead of a number?","acceptedAnswer":{"@type":"Answer","text":"XX marks a day when the round was not held or no result was announced. We preserve those days as XX rather than inventing numbers."}},
            {"@type":"Question","name":"Are these numbers predictions or guaranteed?","acceptedAnswer":{"@type":"Answer","text":"No. This page is a historical record of announced results. It is not a prediction, not a guarantee and not a suggestion of any future outcome."}},
            {"@type":"Question","name":"Where can I see today's live result?","acceptedAnswer":{"@type":"Answer","text":"On the Juwai Teer Result Today homepage at https://jowaiteer.com/."}}
          ]
        })}} />
      </section>

      {/* Related pages */}
      <section className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
        <a href="/" className="card-soft p-4 hover:shadow-md transition" data-testid="link-home">
          <div className="font-bold text-[color:var(--brand-purple-deep)]">Juwai Teer Result Today</div>
          <div className="text-[color:var(--brand-purple)]/85 mt-1">Live FR &amp; SR result for today.</div>
        </a>
        <a href="/common-number/" className="card-soft p-4 hover:shadow-md transition" data-testid="link-common">
          <div className="font-bold text-[color:var(--brand-purple-deep)]">Juwai Teer Common Number</div>
          <div className="text-[color:var(--brand-purple)]/85 mt-1">Daily reference chart.</div>
        </a>
        <a href="/juwai-teer-guide/" className="card-soft p-4 hover:shadow-md transition" data-testid="link-guide">
          <div className="font-bold text-[color:var(--brand-purple-deep)]">Juwai Teer Guide</div>
          <div className="text-[color:var(--brand-purple)]/85 mt-1">Game info, rules, timings.</div>
        </a>
      </section>
    </div>
  );
}
